<div class="page-shell">
  <div class="page-header">
    <div>
      <h1>Managed Feature Flags</h1>
      <p>Find and manage database-backed feature flags for Team Admin and the customer app.</p>
    </div>
    <div class="environment-card">
      <span class="eyebrow">Connected Backend</span>
      <strong>{{ connectedBackend }}</strong>
      <span class="helper-text">Browser environment: {{ connectedEnvironment }}</span>
    </div>
  </div>

  <section class="surface-panel">
    <div class="panel-header">
      <div>
        <h2>Flags</h2>
        <p class="panel-subtitle">
          {{ filteredFlags.length }} of {{ flags.length }} flags match the current view.
        </p>
      </div>
      <div class="header-actions">
        <button mat-stroked-button type="button" (click)="editSection()" [disabled]="loading || saving || loadFailed">
          <mat-icon>create_new_folder</mat-icon> New Section
        </button>
        <button mat-stroked-button type="button" *ngIf="comparisonEnabled" (click)="compareEnvironments()">
          <mat-icon>compare_arrows</mat-icon>
          Compare environments
        </button>
        <button mat-stroked-button type="button" (click)="importFlag()">
          <mat-icon>upload</mat-icon>
          Import Flag
        </button>
        <button mat-raised-button color="primary" type="button" (click)="createFlag()">
          <mat-icon>add</mat-icon>
          New Flag
        </button>
      </div>
    </div>

    <div class="filters-grid">
      <mat-form-field appearance="outline" class="search-field">
        <mat-label>Search flags</mat-label>
        <input
          matInput
          [(ngModel)]="filters.search"
          (ngModelChange)="applyFilters()"
          placeholder="Key, name, or description" />
        <mat-icon matSuffix>search</mat-icon>
      </mat-form-field>

      <mat-form-field appearance="outline">
        <mat-label>App</mat-label>
        <mat-select [(ngModel)]="filters.app" (selectionChange)="applyFilters()">
          <mat-option value="all">All apps</mat-option>
          <mat-option value="team-admin">Team Admin</mat-option>
          <mat-option value="customer-app">Customer App</mat-option>
            <mat-option value="backend">Backend</mat-option>
        </mat-select>
      </mat-form-field>

      <mat-form-field appearance="outline">
        <mat-label>Status</mat-label>
        <mat-select [(ngModel)]="filters.status" (selectionChange)="applyFilters()">
          <mat-option value="all">All statuses</mat-option>
          <mat-option value="active">Active</mat-option>
          <mat-option value="inactive">Inactive</mat-option>
        </mat-select>
      </mat-form-field>

      <mat-form-field appearance="outline">
        <mat-label>Default</mat-label>
        <mat-select [(ngModel)]="filters.defaultState" (selectionChange)="applyFilters()">
          <mat-option value="all">On or off</mat-option>
          <mat-option value="enabled">Default on</mat-option>
          <mat-option value="disabled">Default off</mat-option>
        </mat-select>
      </mat-form-field>
    </div>

    <div class="target-search">
      <mat-form-field appearance="outline">
        <mat-label>Targeting property</mat-label>
        <mat-select [(ngModel)]="targetKind" (selectionChange)="clearTarget()">
          <mat-option value="user">User</mat-option>
          <mat-option value="team">Team</mat-option>
          <mat-option value="company">Company</mat-option>
          <mat-option value="partner">Partner</mat-option>
          <mat-option value="state">State</mat-option>
        </mat-select>
      </mat-form-field>
      <mat-form-field appearance="outline" class="target-query">
        <mat-label>Find flags referencing {{ targetKind }}</mat-label>
        <input matInput [ngModel]="targetQuery" (ngModelChange)="onTargetQueryChange($event)"
          [matAutocomplete]="targetAutocomplete"
          [placeholder]="targetKind === 'state' ? 'State code, e.g. CO' : 'Search by name or ID'" />
        <mat-autocomplete #targetAutocomplete="matAutocomplete" (optionSelected)="selectTarget($event.option.value)"
          [displayWith]="displayTarget">
          <mat-option *ngFor="let option of targetOptions" [value]="option">{{ option.label }}</mat-option>
        </mat-autocomplete>
        <mat-hint *ngIf="!targetId">{{ targetKind === 'user' ? 'Type at least 2 characters for user suggestions.' : 'Select a suggestion or enter an exact value.' }}</mat-hint>
      </mat-form-field>
      <button mat-stroked-button type="button" (click)="useExactTarget()" [disabled]="!targetQuery.trim() || !!targetId">
        {{ targetKind === 'state' ? 'Use state code' : 'Use exact ID' }}
      </button>
      <button mat-button type="button" (click)="clearTarget()" *ngIf="targetQuery || targetId">Clear targeting</button>
    </div>
    <p class="helper-text" role="status" *ngIf="targetLoading">Loading suggestions…</p>
    <p class="organization-error" role="alert" *ngIf="targetError">{{ targetError }}</p>
    <p class="target-search-note" *ngIf="targetId" role="status">Showing saved {{ targetKind }} references to <strong>{{ targetId }}</strong>.</p>
    <p class="organization-note">Targeting search includes both included and excluded values in saved overrides and rules. It does not calculate whether a flag evaluates on for that person or account.</p>

    <p class="organization-note">Sections are shared with other admins on this backend. Expanded and collapsed views are personal.</p>
    <div class="organization-error" *ngIf="organizationError" role="alert">
      {{ organizationError }}
      <button mat-button type="button" (click)="loadFlags()" [disabled]="loading || saving">Refresh</button>
    </div>
    <p class="organization-message" *ngIf="organizationMessage" role="status">{{ organizationMessage }}</p>
    <form class="section-editor" *ngIf="editingSectionId !== null" (ngSubmit)="saveSection()">
      <mat-form-field appearance="outline">
        <mat-label>{{ editingSectionId === 'new' ? 'New section name' : 'Section name' }}</mat-label>
        <input matInput name="sectionName" [(ngModel)]="sectionName" maxlength="160" required [disabled]="saving" />
      </mat-form-field>
      <button mat-raised-button color="primary" type="submit" [disabled]="saving || loading || loadFailed || !sectionName.trim()">Save section</button>
      <button mat-button type="button" (click)="editingSectionId = null" [disabled]="saving">Cancel</button>
    </form>
    <div class="section-toolbar" *ngIf="!loading && !loadFailed">
      <div class="section-view-actions">
        <button mat-button type="button" (click)="expandAll(true)">Expand all</button>
        <button mat-button type="button" (click)="expandAll(false)" [disabled]="!!filters.search.trim() || !!targetId">Collapse all</button>
        <button mat-stroked-button type="button" *ngIf="focusedSection" (click)="focusSection('')">Show all sections</button>
        <span class="helper-text" *ngIf="filters.search.trim() || targetId">Search reveals matching sections.</span>
      </div>
      <div class="bulk-actions" *ngIf="selectedIds.size">
        <strong>{{ selectedIds.size }} selected</strong>
        <mat-form-field appearance="outline">
          <mat-label>Move to section</mat-label>
          <mat-select [(ngModel)]="moveTarget" [disabled]="saving">
            <mat-option value="unsectioned">Unsectioned</mat-option>
            <mat-option *ngFor="let section of sections" [value]="section.id">{{ section.name }}</mat-option>
          </mat-select>
        </mat-form-field>
        <button mat-raised-button color="primary" type="button" (click)="moveSelected()" [disabled]="saving">Move selected</button>
        <button mat-button type="button" (click)="selectedIds.clear()" [disabled]="saving">Clear selection</button>
      </div>
    </div>

    <div class="loading-state" *ngIf="loading">
      <mat-spinner diameter="28"></mat-spinner>
      <span>Loading managed feature flags…</span>
    </div>

    <div class="empty-state error-state" *ngIf="!loading && loadFailed">
      <div>
        <strong>Flags and sections could not be loaded.</strong>
        <p>Check the connected backend and try again.</p>
      </div>
      <button mat-stroked-button type="button" (click)="loadFlags()">Retry</button>
    </div>

    <div class="empty-state" *ngIf="!loading && !loadFailed && groups.length === 0">
      <div>
        <strong>No flags match these filters.</strong>
        <p>Clear the filters to return to the complete list.</p>
      </div>
      <button mat-stroked-button type="button" (click)="clearFilters()">Clear Filters</button>
    </div>

    <ng-container *ngIf="!loading && !loadFailed">
    <section class="flag-section" *ngFor="let group of groups" [attr.aria-label]="group.name">
      <div class="section-header">
        <button class="section-toggle" type="button" (click)="toggleSection(group.id)"
          [disabled]="!!filters.search.trim() || !!targetId" [attr.aria-expanded]="isExpanded(group)"
          [attr.aria-controls]="'section-' + group.id">
          <mat-icon>{{ isExpanded(group) ? 'expand_more' : 'chevron_right' }}</mat-icon>
          <span>{{ group.name }}</span><span class="section-count">{{ group.flags.length }} / {{ group.total }}</span>
        </button>
        <div class="section-actions">
          <button mat-button type="button" (click)="selectGroup(group)" [disabled]="saving || !group.flags.length">Select flags</button>
          <button mat-button type="button" *ngIf="!focusedSection" (click)="focusSection(group.id)">Show only</button>
          <ng-container *ngIf="group.id !== 'unsectioned'">
            <button mat-icon-button type="button" [attr.aria-label]="'Move ' + group.name + ' up'" (click)="moveSection(group.id, -1)"
              [disabled]="saving || sections[0].id === group.id"><mat-icon>arrow_upward</mat-icon></button>
            <button mat-icon-button type="button" [attr.aria-label]="'Move ' + group.name + ' down'" (click)="moveSection(group.id, 1)"
              [disabled]="saving || sections[sections.length - 1].id === group.id"><mat-icon>arrow_downward</mat-icon></button>
            <button mat-button type="button" (click)="editSection(group.id)" [disabled]="saving">Rename</button>
            <button mat-button type="button" (click)="deleteSection(group)" [disabled]="saving">Delete</button>
          </ng-container>
        </div>
      </div>
      <div [id]="'section-' + group.id" [hidden]="!isExpanded(group)">
        <p class="section-empty" *ngIf="!group.flags.length">No flags in this section. Select flags in another section to move them here.</p>
    <div class="table-wrap" *ngIf="group.flags.length">
      <table class="flags-table">
        <thead>
          <tr>
            <th scope="col">Flag</th>
            <th scope="col">Apps</th>
            <th scope="col">Status</th>
            <th scope="col">Default</th>
            <th scope="col">Targeting</th>
            <th scope="col">Last modified</th>
            <th scope="col"><span class="visually-hidden">Open</span></th>
          </tr>
        </thead>
        <tbody>
          <tr
            *ngFor="let flag of group.flags; trackBy: trackByFlagId"
            class="flag-row"
            tabindex="0"
            (click)="openFlag(flag)"
            (keydown.enter)="openFlag(flag)"
            (keydown.space)="openFlag(flag); $event.preventDefault()">
            <td data-label="Flag">
              <mat-checkbox class="flag-checkbox" [checked]="selectedIds.has(flag.id)" [disabled]="saving"
                [aria-label]="'Select ' + flag.key" (click)="$event.stopPropagation()" (keydown)="$event.stopPropagation()"
                (change)="selectFlag(flag.id, $event.checked)"></mat-checkbox>
              <strong class="flag-key">{{ flag.key }}</strong>
              <span class="flag-name">{{ flag.name }}</span>
              <span class="flag-description" *ngIf="flag.description">{{ flag.description }}</span>
            </td>
            <td data-label="Apps">
              <div class="app-pills">
                <span class="app-pill" *ngFor="let app of flag.appTargets">
                  {{ app === 'backend' ? 'Backend' : app === 'team-admin' ? 'Team Admin' : 'Customer App' }}
                </span>
              </div>
            </td>
            <td data-label="Status">
              <span class="status-pill" [class.status-pill-inactive]="!flag.isActive">
                {{ flag.isActive ? 'Active' : 'Inactive' }}
              </span>
            </td>
            <td data-label="Default">
              <span class="status-pill status-pill-default" [class.status-pill-default-off]="!flag.defaultEnabled">
                {{ flag.defaultEnabled ? 'On' : 'Off' }}
              </span>
            </td>
            <td data-label="Targeting">
              <span class="targeting-summary">
                {{ flag.rules.length }} {{ flag.rules.length === 1 ? 'rule' : 'rules' }}
                <span aria-hidden="true">·</span>
                {{ getOverrideCount(flag) }} {{ getOverrideCount(flag) === 1 ? 'override' : 'overrides' }}
              </span>
              <span class="target-match" *ngFor="let match of targetMatches(flag)">{{ match }}</span>
            </td>
            <td data-label="Last modified">
              <span class="modified-date">{{ flag.modifiedAt | date:'mediumDate' }}</span>
              <span class="modified-time">{{ flag.modifiedAt | date:'shortTime' }}</span>
            </td>
            <td class="open-cell">
              <button
                mat-icon-button
                type="button"
                [attr.aria-label]="'Edit ' + flag.key"
                (click)="$event.stopPropagation(); openFlag(flag)">
                <mat-icon>chevron_right</mat-icon>
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
      </div>
    </section>
    </ng-container>
  </section>
</div>
